/* eslint-disable prettier/prettier */
import React, { useImperativeHandle, useRef, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { makeStyles, CircularProgress, Box, Tab } from '@material-ui/core';
import {
  FormikField,
  BaseCardField,
  BaseInputLabel,
  BaseTypography,
  BaseInputsGroup,
} from 'src/components/common';
import { useFormikErrorsHandler } from 'src/utils/hooks/useFormikErrorsHandler';
import { ExpertisesMultiSelect } from 'src/components/fields/selects/ExpertisesMultiSelect';
import { CountriesMultiSelect } from 'src/components/fields/selects/CountriesMultiSelect';
import { Rating, TabContext, TabList, TabPanel } from '@material-ui/lab';
import { ICreateGuestModel, IGuestInfo } from 'src/api/base/Guest/types';
import { IBaseForm } from 'src/utils/types/forms';
import { FilesUploader } from 'src/components/common/FilesUploader';
import { capitalizeFirstLetter } from 'src/utils/helpers';
import { LanguagesMultiSelect } from 'src/components/fields/selects/LanguagesMultiSelect/LanguagesMultiSelect';
import { BaseDateTimePicker } from 'src/components/common/BaseDateTimePicker/BaseDateTimePicker';
import { BaseHelperText } from 'src/components/common/BaseTextField/BaseHelperText/BaseHelperText';
import { AvailableForCheckboxesContainer } from 'src/components/fields/other/AvailableForCheckboxes/AvailableForCheckboxesContainer';
import { GenderSelect } from 'src/components/fields/selects/GenderSelect';
import { newContactValidationSchema } from 'src/features/user/journalist/newContact/newContactValidationSchema';
import defaultAvatar from 'src/assets/images/no-user-picture.svg';
import clsx from 'clsx';
import { useDispatch } from 'react-redux';

import { IOSSwitch } from 'src/components/common/BaseButton/IosSwitch';

import { ExpertisesApi } from 'src/api/base/Expertises';
import FormikHierarchySelect from 'src/features/user/journalist/profile/JournalistInfoForm/FormikHierarchySelect';

const useStyles = makeStyles((theme) => ({
  formGroups: {
    display: 'grid',
    gridTemplateColumns: '128px 1fr 1fr',
    gridGap: '32px',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
    '& label p': {
      color: '#6A7282 !important',
    },
    '& input': {
      color: '#000000 !important',
    },
    '& .MuiSelect-select span': {
      color: '#000000 !important',
    },
    '& .MuiRating-icon.MuiRating-iconFilled .MuiSvgIcon-root': {
      fill: '#FDB44B !important',
    },
    '& .MuiRating-icon.MuiRating-iconEmpty .MuiSvgIcon-root': {
      fill: '#D1D5DC !important',
    },
  }, formGroups2: {
    gridTemplateColumns: '1fr 1fr',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  formGroups3: {
    gridTemplateColumns: '1fr 1fr',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  formGroup: {
    display: 'grid',
    gridGap: '20px',
    gridAutoRows: 'max-content',
  },
  submitSection: {
    marginTop: 'auto',
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(100px, 200px)',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
      gridGap: '20px',
    },
  },
  available: {
    'boxShadow': 'none',
    'padding': '0 !important',
    '& .MuiFormControlLabel-label': {
      fontSize: '14px !important',
    }
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    color: '#4F4F4F',
  },
  inputField: {
    display: 'none',
  },
  otherText: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '16px',
    marginTop: '20px',
  },
  uploadPictureBtn: {
    color: '#4A9EDB',
    cursor: 'pointer',
    marginTop: '16px',
    fontWeight: 600,
  },
  deletePictureBtn: {
    color: '#FF3B30',
    cursor: 'pointer',
    marginTop: '10px',
    fontWeight: 600,
  },
  userImg: {
    maxWidth: '100%',
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  avatar: {
    position: 'relative',
    width: '128px',
    height: '128px',
    borderRadius: '50%',
    overflow: 'hidden',
  },
  avatar__progress: {
    position: 'absolute',
    zIndex: 3,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    background: 'black',
    opacity: 0.8,
    top: 0,
  },
  borderBottomLine: {
    paddingBottom: '24px',
    borderBottom: '1px solid #E5E7EB',
    marginBottom: 24,
  }, customUpload: {
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '8px',
    border: '2px dashed #DAEFFB',
    cursor: 'pointer',
    '& .heading': {
      fontSize: 14,
      fontWeight: 600,
      color: '#000'
    }, '& .sub': {
      fontSize: 10,
      fontWeight: 400,
      color: '#8E8E93'
    }
  }, customUpoadContainer: {
    display: 'flex',
    gap: '50px',
    '& > .uploadContainer': {
      width: '35%'
    }, '& > .addByUrl': {
      width: '50%'
    },

    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      '& > div': {
        width: '100% !important'
      }
    },
  }, file__delete: {
    position: 'absolute',
    top: '7px',
    right: '7px',
    padding: 3,
    borderRadius: '100%',
    background: '#FF3B30',
    '& > .delIcon': {
      width: 16,
      height: 16,
      cursor: 'pointer',
      position: 'relative',
      zIndex: 1,
    }
  }, tabRoot: {
    minWidth: '150px',
    fontWeight: 600,
    fontSize: '16px',
    color: '#8E8E93',
    textTransform: 'capitalize',
    '&.Mui-selected': {
      color: '#4A9EDB',
    },
  },
  tabIndicator: {
    backgroundColor: '#4A9EDB',
    height: '2px',
  },
}));

type INewContactCreateForm = IBaseForm<ICreateGuestModel> & {
  guest?: IGuestInfo | null;
  isCreatingForm?: boolean;
  saveRef?: any;
};

const formInitialValues = {
  first_name: '',
  last_name: '',
  gender: '',
  title: '',
  company_name: '',
  linkedin: '',
  expertise: [],
  available: [],
  country: [],
  description: '',
  rating: 4,
  is_company_logo_visible: false,
  is_company_description_visible: false,

  guest_company_description: '',
  guest_company_logo: '',
  manager_company_description: '',
  manager_company_logo: '',
  media_license_expiry: '',
  training_certificate: '',
  languages: [],
  last_date_trained: '',
  cba_lead: '',
  onedrive_link: '',
  background_briefings_story: '',
  images: [],
  videos: [],
};

const formKeys = Object.keys(formInitialValues);

export const NewContactCreateForm: React.FC<INewContactCreateForm> = (props) => {
  const {
    onSubmit,
    onUpdateAvatar,
    onUpdateImagesFormData,
    onUpdateVideosFormData,
    loading,
    errors,
    guest,
    saveRef,
  } = props;

  const [isGuest, setIsGuest] = useState<boolean>(false);

  useEffect(() => {
    const url = window.location.pathname;
    setIsGuest(url === "/guests/new-guest/create");
  }, []);

  // Init Section

  const classes = useStyles();
  const formik = useFormik<ICreateGuestModel>({
    initialValues: { ...formInitialValues },
    validationSchema: newContactValidationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      const localGuest = guest || {};
      onSubmit({ ...localGuest, ...values });
      formik.setErrors({});
    },
  });

  const { setFieldError } = formik;

  useFormikErrorsHandler({ errors, setFieldError });

  const [hover, setHover] = React.useState<number>(5);

  // End of Init Section

  // Select Avatar Image
  const [isImgLoading, setImgLoading] = useState(false);

  const [selectedAvatarImg, setSelectedAvatarImg] = useState({
    selectedFile: null,
    src: '',
  });

  const handleFileChange = (e: any) => {
    setImgLoading(true);
    const selectedFile = e.target.files[0];
    const createdLocalURI = URL.createObjectURL(selectedFile);
    setSelectedAvatarImg({ selectedFile, src: createdLocalURI });

    const formData = new FormData();
    formData.append('avatar', e.target.files[0]);
    onUpdateAvatar!(formData);
    setImgLoading(false);
  };

  const onDelete = () => setSelectedAvatarImg({ selectedFile: null, src: '' });
  // End of Select Avatar Image

  // Upload Selected Photos 
  const [tabValue, setValue] = useState<string>('1');

  const [selectedImages, setSelectedImages] = useState<any[]>([]);

  const deleteSelectedImage = (uri: string) => {
    URL.revokeObjectURL(uri);

    const updatedImages = selectedImages.filter((file) => file.src !== uri);
    setSelectedImages(updatedImages);

    const newFormData = new FormData();
    updatedImages.forEach((item) => {
      newFormData.append('images[]', item.image);
    });
    
    onUpdateImagesFormData!(newFormData);
  };

  const handleSelectPhotos = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const newFiles = Array.from(e.target.files).map((file) => ({
      image: file,
      src: URL.createObjectURL(file),
    }));

    const updatedImages = [...selectedImages, ...newFiles];
    setSelectedImages(updatedImages);

    const newFormData = new FormData();
    updatedImages.forEach((item) => {
      newFormData.append('images[]', item.image);
    });

    onUpdateImagesFormData!(newFormData);
  };
  // End of Upload Selected Photos 

  // Upload Selected Videos 
  const [selectedVideos, setSelectedVideos] = useState<any[]>([]); 
  
  const deleteSelectedVideo = (uri: string) => {
    URL.revokeObjectURL(uri);

    const updatedVideos = selectedVideos.filter((file) => file.src !== uri);
    setSelectedVideos(updatedVideos);

    const newFormData = new FormData();
    updatedVideos.forEach((item) => {
      newFormData.append('videos[]', item.image);
    });
    onUpdateVideosFormData!(newFormData);
  };

  const handleSelectVideos = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const newFiles = Array.from(e.target.files).map((file) => ({
      image: file,
      src: URL.createObjectURL(file),
    }));

    const updatedVideos = [...selectedVideos, ...newFiles];
    setSelectedVideos(updatedVideos);

    const newFormData = new FormData();
    updatedVideos.forEach((item) => {
      newFormData.append('videos[]', item.image);
    });

    onUpdateVideosFormData!(newFormData);
  };

  // End of Upload Selected Videos 

  const inputRef = useRef(null);
  const handleUploadPictureClick = () => {
    // @ts-ignore
    inputRef?.current.click();
  };

  // Button References

  const inputRefImg = useRef<HTMLInputElement>(null);
  const handleUploadBtnClickImg = () => {
    // @ts-ignore
    inputRefImg?.current.click();
  };

  const inputRefVid = useRef<HTMLInputElement>(null);
  const handleUploadBtnClickVid = () => {
    // @ts-ignore
    inputRefVid?.current.click();
  };


  // submit handle 
  useImperativeHandle(saveRef, () => ({
    submit: () => {
      formik.handleSubmit();
    },
  }));
  
  const [expertiseTree, setExpertiseTree] = useState([]);
  	
	const loadExpertises = async () => {
	  try {
		const expertiseresponse = await ExpertisesApi.getExpertisesTree();

		if (expertiseresponse) {
		  setExpertiseTree(expertiseresponse.data.data || []);
		} else {
		  setExpertiseTree([]);
		}

	  } catch (error) {
		setExpertiseTree([]);
	  }
	};
	
	useEffect(() => {
	  loadExpertises();
	}, []);

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <div className={clsx(classes.formGroups, classes.borderBottomLine)}>
          <div className={classes.formGroup}>
            <div className="profilePhoto">
              <div className={classes.root}>
                <div className={classes.avatar}>
                  {!loading && <img
                    src={selectedAvatarImg.src || defaultAvatar}
                    alt="User img"
                    className={classes.userImg}
                  />}

                  {(loading || isImgLoading) && (
                    <div className={classes.avatar__progress}>
                      <CircularProgress />
                    </div>
                  )}
                </div>

                <input
                  ref={inputRef}
                  type="file"
                  className={classes.inputField}
                  onChange={handleFileChange}
                />

                <div
                  className={clsx(classes.otherText, classes.uploadPictureBtn)}
                  onClick={handleUploadPictureClick}>
                  Select photo
                </div>
                <div
                  className={clsx(classes.otherText, classes.deletePictureBtn)}
                  onClick={onDelete}>
                  Delete photo
                </div>
              </div>
            </div>
          </div>
          <div className={classes.formGroup}>
            <FormikField
              label="First name*"
              name="first_name"
              formik={formik}
            />
            <FormikField label="Last name*" name="last_name" formik={formik} />
            <FormikField label="Title*" name="title" formik={formik} />

            <FormikField
              label="Company name*"
              name="company_name"
              formik={formik}
            />
          </div>
          <div className={classes.formGroup}>
            <FormikField
              label="Gender*"
              name="gender"
              formik={formik}
              inputComponent={GenderSelect}
              onChange={(newValue: string) => {
                formik.setFieldValue('gender', newValue);
              }}
            />
            <FormikField
              inputComponent={CountriesMultiSelect}
              name="country"
              formik={formik}
              label="Country of residence*"
              onChange={(value: any) => {
                formik.setFieldValue('country', value);
              }}
            />
            <FormikField
              label="Languages"
              name="languages"
              formik={formik}
              inputComponent={LanguagesMultiSelect}
              onChange={(value: any) => {
                formik.setFieldValue('languages', value);
              }}
            />
            <FormikField
              label="LinkedIn profile link*"
              name="linkedin"
              formik={formik}
            />
          </div>
        </div>
        <div>
          <BaseTypography style={{ color: '#4A9EDB', fontSize: '18px', fontWeight: '600', marginBottom: '24px', lineHeight: 'normal' }} variant="h3" >
            Key Information
          </BaseTypography>

          <div className={clsx(classes.formGroups, classes.formGroups2)}>
          {isGuest && (
              <>
              <div className={classes.formGroup}>
                <FormikField
                  label="Media strategy"
                  name="cba_lead"
                  formik={formik}
                  disabled
                />
                <div >
                  <div style={{ marginBottom: '8px', color: '#6A7282' }}>Last date trained</div>
                  <BaseDateTimePicker
                    format="yyyy-MM-dd"
                    errorText={formik.errors.last_date_trained as string}
                    error={Boolean(formik.errors.last_date_trained)}
                    placeholder="Select date and time"
                    value={(formik?.values?.last_date_trained && formik?.values?.last_date_trained !== '0000-00-00') && formik?.values?.last_date_trained}
                    disabled
                  />
                </div>
                <div >
                  <div style={{ marginBottom: '8px', color: '#6A7282' }}>Media License Expiry</div>
                  <BaseDateTimePicker
                    format="yyyy-MM-dd"
                    errorText={formik.errors.media_license_expiry as string}
                    error={Boolean(formik.errors.media_license_expiry)}
                    placeholder="Select date and time"
                    value={(formik?.values?.media_license_expiry && formik?.values?.media_license_expiry !== '0000-00-00') && formik?.values?.media_license_expiry}
                    disabled
                  />
                </div>

              </div>
            </>
          )}
            <div className={classes.formGroup}>
              <BaseCardField
                label="Rating"
                errorText={formik.errors.rating}
                cardOptions={{ offCardStyles: true }}>
                <Rating
                  name="rating"
                  size="large"
                  value={formik.values.rating}
                  precision={1}
                  onChangeActive={(_, newHover) => {
                    setHover(newHover);
                  }}
                  onClick={() => {
                    formik.setFieldValue('rating', hover);
                  }}
                />
              </BaseCardField>
              <AvailableForCheckboxesContainer
                value={formik.values.available}
                errorText={formik.errors.available}
                onChange={(value: any) => {
                  formik.setFieldValue('available', value);
                }}
                offCardStyles={false}
                className={classes.available}
              />

              { /* <FormikField
                name="expertise"
                formik={formik}
                label="Areas of expertise*"
                inputComponent={ExpertisesMultiSelect}
                onChange={(value: any) => {
                  formik.setFieldValue('expertise', value);
                }}
              /> */ }
			  
			  <FormikHierarchySelect
				name="expertise"
				label="Areas of expertise*"
				formik={formik}
				options={expertiseTree}
			  />
            </div>
          </div>

          <div className={classes.borderBottomLine}>
            <p style={{ color: '#6A7282' }}>Unique insights*</p>
            <FormikField
              multiline
              rowsMin={6}
              rows={6}
              name="description"
              formik={formik}
              InputProps={{
                style: {
                  height: '136px',
                },
              }}
            />
            <p style={{ textAlign: 'right', color: '#8E8E93' }}>
              Characters remaining:{' '}
              {510 - formik.values?.description?.length < 0
                ? '0'
                : 510 - formik.values?.description?.length}
            </p>
          </div>
        </div>
        <div>

          {isGuest && (
            <>
              <BaseTypography style={{ color: '#4A9EDB', fontSize: '18px', fontWeight: '600', marginBottom: '24px', lineHeight: 'normal' }} variant="h3" >
                Training Information
              </BaseTypography>

              <div className={clsx(classes.formGroups, classes.formGroups3, classes.borderBottomLine)}>
                <div className={classes.formGroup}>
                  <FormikField label="Training Certificate" disabled name="training_certificate" formik={formik} />
                  <FormikField
                    label="Training videos"
                    name="onedrive_link"
                    formik={formik}
                    disabled
                  />
                  <FormikField
                    label="Background briefing"
                    name="background_briefings_story"
                    formik={formik}
                    disabled
                  />
                </div>
              </div>

            </>
          )}

        </div>

        <div className={clsx(classes.borderBottomLine)}>
          <BaseTypography style={{ color: '#4A9EDB', fontSize: '18px', fontWeight: '600', marginBottom: '24px', lineHeight: 'normal' }} variant="h3" >
            Company Information
          </BaseTypography>

          {formik.values.is_company_logo_visible && (
            <>
              <div>
                <BaseInputLabel>Company logo</BaseInputLabel>
                <FilesUploader
                  accept="image/*"
                  onChooseFile={(files) => {
                    formik.setFieldValue(
                      'guest_company_logo',
                      files.length ? files[0] : '',
                    );
                  }}
                  files={formik.values.guest_company_logo}
                  multiple={false}
                />
                {formik.errors.guest_company_logo && (
                  <BaseHelperText error>
                    {capitalizeFirstLetter(
                      formik.errors.guest_company_logo || '',
                    )}
                  </BaseHelperText>
                )}
              </div>
            </>
          )}

          <BaseTypography variant="caption" className="mt-8" style={{ margin: '16px 0' }}>
            Show in profile?<span><IOSSwitch name="is_company_logo_visible" checked={formik.values.is_company_logo_visible} onChange={formik.handleChange} /></span>
          </BaseTypography>

          {formik.values.is_company_description_visible && (
            <FormikField
              label="Company Description"
              name="guest_company_description"
              formik={formik}
              multiline
              rowsMin={6}
              rows={6}
              InputProps={{
                style: {
                  height: '136px',
                },
              }}
            />
          )}

          <BaseTypography variant="caption" className="mt-8" style={{ margin: '16px 0' }}>
            Show in profile?<span><IOSSwitch name="is_company_description_visible" checked={formik.values.is_company_description_visible} onChange={formik.handleChange} /></span>
          </BaseTypography>
        </div>

        <div>
          <BaseTypography style={{ color: '#4A9EDB', fontSize: '18px', fontWeight: '600', lineHeight: 'normal', marginBottom: 24 }} variant="h3" >
            Gallery
          </BaseTypography>
          <Box sx={{ width: '100%' }}>
            <TabContext value={tabValue} >
              <Box className={classes.tabsContainer}>
                <TabList onChange={(_, newValue) => setValue(newValue)} aria-label="lab API tabs example" TabIndicatorProps={{ className: classes.tabIndicator }}
                  variant="standard" >
                  <Tab label="Photos" value="1" classes={{ root: classes.tabRoot }} />
                  <Tab label="Videos" value="2" classes={{ root: classes.tabRoot }} />
                </TabList>
              </Box>

              <TabPanel value="1" style={{ padding: '24px 0' }}>
                <div className="containerWrapper">
                  <div>
                    <BaseTypography style={{ fontSize: 16, color: '#8E8E93', marginBottom: 14 }} variant='text' >
                      Selected photos
                    </BaseTypography>
                    <div style={{ display: 'flex', gap: '24px', borderBottom: '1px solid #E5E7EB', paddingBottom: '24px', marginBottom: '24px', flexWrap: 'wrap' }}>
                      {selectedImages.length > 0 && selectedImages.map((file) => {
                        return <div style={{ width: '150px', height: '150px', borderRadius: '8px', overflow: 'hidden', position: 'relative' }} key={file.src}>
                          <div className={classes.file__delete}>
                            <div
                              className='delIcon' onClick={() => deleteSelectedImage(file.src)}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <rect width="16" height="16" rx="8" fill="#FF3B30" />
                                <path d="M5.1875 13C4.87812 13 4.61337 12.8913 4.39325 12.6739C4.17312 12.4565 4.06287 12.1948 4.0625 11.8889V4.66667H3.5V3.55556H6.3125V3H9.6875V3.55556H12.5V4.66667H11.9375V11.8889C11.9375 12.1944 11.8274 12.4561 11.6073 12.6739C11.3872 12.8917 11.1223 13.0004 10.8125 13H5.1875ZM10.8125 4.66667H5.1875V11.8889H10.8125V4.66667ZM6.3125 10.7778H7.4375V5.77778H6.3125V10.7778ZM8.5625 10.7778H9.6875V5.77778H8.5625V10.7778Z" fill="white" />
                              </svg>
                            </div>
                          </div>
                          <img src={file.src} alt='' style={{ objectFit: 'contain', objectPosition: 'top', width: '100%' }} />
                        </div>
                      })
                      }
                    </div>
                  </div>
                  <BaseTypography style={{ fontSize: 16, color: '#8E8E93', marginBottom: 14 }} variant='text' >
                    Add new photos
                  </BaseTypography>
                  <div className={classes.customUpoadContainer}>
                    <div className="uploadContainer">
                      <input
                        ref={inputRefImg}
                        type="file"
                        className={classes.inputField}
                        onChange={handleSelectPhotos}
                        accept='image/*'
                        multiple
                      />
                      <div className={classes.customUpload} onClick={handleUploadBtnClickImg}>
                        <div className="svg"><svg xmlns="http://www.w3.org/2000/svg" width="47" height="41" viewBox="0 0 47 41" fill="none">
                          <path d="M30.9458 3.77771C31.2999 3.77771 31.5874 4.06525 31.5874 4.41931C31.5874 4.77336 31.2999 5.06091 30.9458 5.06091C28.2926 5.06109 25.9024 6.77987 25.0464 9.27087L24.9683 9.51404C24.8776 9.81989 24.579 10.0053 24.271 9.96423L24.2632 9.974L24.1714 9.94568C23.8323 9.84453 23.6387 9.48826 23.7397 9.1488C24.6612 6.05237 27.5307 3.77789 30.9458 3.77771Z" fill="#4A9EDB" stroke="white" strokeWidth="0.3" />
                          <path d="M37.8229 28.4884H34.8757C34.6045 28.4884 34.3845 28.2684 34.3845 27.9972C34.3845 27.726 34.6045 27.506 34.8757 27.506H37.8229C41.8854 27.506 45.1909 24.2005 45.1909 20.138C45.1909 16.0754 41.8854 12.7699 37.8229 12.7699H37.752C37.6096 12.7699 37.4741 12.7082 37.3808 12.6005C37.2875 12.4928 37.2454 12.35 37.2657 12.2089C37.3096 11.903 37.3317 11.5956 37.3317 11.2963C37.3317 7.77549 34.4669 4.91068 30.946 4.91068C29.5763 4.91068 28.27 5.33873 27.1683 6.14885C26.9262 6.32674 26.5823 6.2478 26.4424 5.98149C23.3222 0.0400754 15.1726 -0.757796 10.9475 4.41072C9.16764 6.58813 8.4683 9.42061 9.02869 12.1812C9.09044 12.4861 8.85711 12.7703 8.54731 12.7703H8.35047C4.28792 12.7703 0.982451 16.0758 0.982451 20.1383C0.982451 24.2009 4.28792 27.5064 8.35047 27.5064H11.2976C11.5689 27.5064 11.7889 27.7263 11.7889 27.9976C11.7889 28.2688 11.5689 28.4888 11.2976 28.4888H8.35047C3.74612 28.4888 0 24.7427 0 20.1383C0 15.6631 3.53873 11.9987 7.96488 11.7966C7.54911 8.93254 8.34416 6.04357 10.1868 3.789C14.7104 -1.74508 23.3796 -1.12478 27.0443 5.04612C28.2134 4.31317 29.5511 3.92867 30.9459 3.92867C35.2116 3.92867 38.5918 7.55937 38.296 11.8012C42.6814 12.0472 46.1731 15.6922 46.1731 20.1379C46.1731 24.7427 42.427 28.4884 37.8227 28.4884L37.8229 28.4884Z" fill="#4A9EDB" />
                          <path d="M23.021 15.2892C29.8751 15.2892 35.4507 20.8657 35.4507 27.7198C35.4506 34.5739 29.8751 40.1495 23.021 40.1495C16.1669 40.1495 10.5904 34.574 10.5903 27.7198C10.5903 20.8657 16.1668 15.2892 23.021 15.2892ZM23.021 16.5724C16.8744 16.5724 11.8735 21.5733 11.8735 27.7198C11.8736 33.8664 16.8745 38.8673 23.021 38.8673C29.1675 38.8673 34.1684 33.8663 34.1685 27.7198C34.1685 21.5733 29.1675 16.5724 23.021 16.5724Z" fill="#4A9EDB" stroke="white" strokeWidth="0.3" />
                          <path d="M23.2454 22.8032C23.5267 22.8032 23.755 23.0317 23.7551 23.313V32.6636C23.755 32.9452 23.5266 33.1733 23.2454 33.1733C22.9642 33.1732 22.7357 32.9448 22.7356 32.6636V23.313C22.7357 23.0318 22.9641 22.8034 23.2454 22.8032Z" fill="#4A9EDB" stroke="#4A9EDB" strokeWidth="0.3" />
                          <path d="M22.8848 22.9535C23.0836 22.7544 23.4065 22.7548 23.6055 22.9535L26.4824 25.8304C26.6815 26.0295 26.6815 26.3521 26.4824 26.5511C26.3828 26.651 26.2519 26.7006 26.1221 26.7006C25.992 26.7005 25.8611 26.6505 25.7617 26.5511L23.2451 24.0345L20.7285 26.5511C20.5296 26.7503 20.2068 26.7501 20.0078 26.5511C19.809 26.3521 19.8088 26.0294 20.0078 25.8304L22.8848 22.9535Z" fill="#4A9EDB" stroke="#4A9EDB" strokeWidth="0.3" />
                        </svg></div>
                        <div className="heading">Upload files</div>
                        <div className="sub">Supported formates: SVG, PNG, JPG</div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabPanel>

              <TabPanel value="2" style={{ padding: '24px 0' }}>
                <div className="containerWrapper">
                  <div>
                    <BaseTypography style={{ fontSize: 16, color: '#8E8E93', marginBottom: 14 }} variant='text' >
                      Selected videos
                    </BaseTypography>
                    <div style={{ display: 'flex', gap: '24px', borderBottom: '1px solid #E5E7EB', paddingBottom: '24px', marginBottom: '24px', flexWrap: 'wrap' }}>
                      {
                        (selectedVideos.length > 0) && selectedVideos.map((file) => {
                          return <div style={{ width: '150px', height: '150px', borderRadius: '8px', overflow: 'hidden', position: 'relative' }} key={file.src}>
                            <div className={classes.file__delete}>
                              <div
                                className='delIcon' onClick={() => deleteSelectedVideo(file.src)}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <rect width="16" height="16" rx="8" fill="#FF3B30" />
                                  <path d="M5.1875 13C4.87812 13 4.61337 12.8913 4.39325 12.6739C4.17312 12.4565 4.06287 12.1948 4.0625 11.8889V4.66667H3.5V3.55556H6.3125V3H9.6875V3.55556H12.5V4.66667H11.9375V11.8889C11.9375 12.1944 11.8274 12.4561 11.6073 12.6739C11.3872 12.8917 11.1223 13.0004 10.8125 13H5.1875ZM10.8125 4.66667H5.1875V11.8889H10.8125V4.66667ZM6.3125 10.7778H7.4375V5.77778H6.3125V10.7778ZM8.5625 10.7778H9.6875V5.77778H8.5625V10.7778Z" fill="white" />
                                </svg>
                              </div>
                            </div>
                            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                            <a href={file.src} target="_blank" rel="noopener noreferrer">
                              <video style={{ width: '100%', height: '100%' }}><source src={file.src} type="video/mp4" />
                                <track
                                  kind="captions"
                                  // src="/path/to/captions.vtt" 
                                  srcLang="en"
                                  label="English"
                                  default
                                /></video>
                            </a>
                          </div>
                        })
                      }
                    </div>
                  </div>
                  <BaseTypography style={{ fontSize: 16, color: '#8E8E93', marginBottom: 14 }} variant='text' >
                    Add new videos
                  </BaseTypography>
                  <div className={classes.customUpoadContainer}>
                    <div className="uploadContainer">
                      <input
                        ref={inputRefVid}
                        type="file"
                        className={classes.inputField}
                        onChange={handleSelectVideos}
                        accept='video/*'
                        multiple
                      />
                      <div className={classes.customUpload} onClick={handleUploadBtnClickVid}>
                        <div className="svg"><svg xmlns="http://www.w3.org/2000/svg" width="47" height="41" viewBox="0 0 47 41" fill="none">
                          <path d="M30.9458 3.77771C31.2999 3.77771 31.5874 4.06525 31.5874 4.41931C31.5874 4.77336 31.2999 5.06091 30.9458 5.06091C28.2926 5.06109 25.9024 6.77987 25.0464 9.27087L24.9683 9.51404C24.8776 9.81989 24.579 10.0053 24.271 9.96423L24.2632 9.974L24.1714 9.94568C23.8323 9.84453 23.6387 9.48826 23.7397 9.1488C24.6612 6.05237 27.5307 3.77789 30.9458 3.77771Z" fill="#4A9EDB" stroke="white" strokeWidth="0.3" />
                          <path d="M37.8229 28.4884H34.8757C34.6045 28.4884 34.3845 28.2684 34.3845 27.9972C34.3845 27.726 34.6045 27.506 34.8757 27.506H37.8229C41.8854 27.506 45.1909 24.2005 45.1909 20.138C45.1909 16.0754 41.8854 12.7699 37.8229 12.7699H37.752C37.6096 12.7699 37.4741 12.7082 37.3808 12.6005C37.2875 12.4928 37.2454 12.35 37.2657 12.2089C37.3096 11.903 37.3317 11.5956 37.3317 11.2963C37.3317 7.77549 34.4669 4.91068 30.946 4.91068C29.5763 4.91068 28.27 5.33873 27.1683 6.14885C26.9262 6.32674 26.5823 6.2478 26.4424 5.98149C23.3222 0.0400754 15.1726 -0.757796 10.9475 4.41072C9.16764 6.58813 8.4683 9.42061 9.02869 12.1812C9.09044 12.4861 8.85711 12.7703 8.54731 12.7703H8.35047C4.28792 12.7703 0.982451 16.0758 0.982451 20.1383C0.982451 24.2009 4.28792 27.5064 8.35047 27.5064H11.2976C11.5689 27.5064 11.7889 27.7263 11.7889 27.9976C11.7889 28.2688 11.5689 28.4888 11.2976 28.4888H8.35047C3.74612 28.4888 0 24.7427 0 20.1383C0 15.6631 3.53873 11.9987 7.96488 11.7966C7.54911 8.93254 8.34416 6.04357 10.1868 3.789C14.7104 -1.74508 23.3796 -1.12478 27.0443 5.04612C28.2134 4.31317 29.5511 3.92867 30.9459 3.92867C35.2116 3.92867 38.5918 7.55937 38.296 11.8012C42.6814 12.0472 46.1731 15.6922 46.1731 20.1379C46.1731 24.7427 42.427 28.4884 37.8227 28.4884L37.8229 28.4884Z" fill="#4A9EDB" />
                          <path d="M23.021 15.2892C29.8751 15.2892 35.4507 20.8657 35.4507 27.7198C35.4506 34.5739 29.8751 40.1495 23.021 40.1495C16.1669 40.1495 10.5904 34.574 10.5903 27.7198C10.5903 20.8657 16.1668 15.2892 23.021 15.2892ZM23.021 16.5724C16.8744 16.5724 11.8735 21.5733 11.8735 27.7198C11.8736 33.8664 16.8745 38.8673 23.021 38.8673C29.1675 38.8673 34.1684 33.8663 34.1685 27.7198C34.1685 21.5733 29.1675 16.5724 23.021 16.5724Z" fill="#4A9EDB" stroke="white" strokeWidth="0.3" />
                          <path d="M23.2454 22.8032C23.5267 22.8032 23.755 23.0317 23.7551 23.313V32.6636C23.755 32.9452 23.5266 33.1733 23.2454 33.1733C22.9642 33.1732 22.7357 32.9448 22.7356 32.6636V23.313C22.7357 23.0318 22.9641 22.8034 23.2454 22.8032Z" fill="#4A9EDB" stroke="#4A9EDB" strokeWidth="0.3" />
                          <path d="M22.8848 22.9535C23.0836 22.7544 23.4065 22.7548 23.6055 22.9535L26.4824 25.8304C26.6815 26.0295 26.6815 26.3521 26.4824 26.5511C26.3828 26.651 26.2519 26.7006 26.1221 26.7006C25.992 26.7005 25.8611 26.6505 25.7617 26.5511L23.2451 24.0345L20.7285 26.5511C20.5296 26.7503 20.2068 26.7501 20.0078 26.5511C19.809 26.3521 19.8088 26.0294 20.0078 25.8304L22.8848 22.9535Z" fill="#4A9EDB" stroke="#4A9EDB" strokeWidth="0.3" />
                        </svg></div>
                        <div className="heading">Upload files</div>
                        <div className="sub">Supported formates: MP4, MOV</div>
                      </div>
                    </div>
                    <div className="addByUrl">
                      <BaseInputsGroup
                        value={formik.values.videos || ['']}
                        onChange={(values) => formik.setFieldValue('videos', values)}
                        formik={formik}
                        inputLabel="Add by URL"
                        name="videos"
                        addMoreText="+ Add more"
                        disableDelete
                      />
                    </div>
                  </div>
                </div>
              </TabPanel>
            </TabContext>
          </Box>
        </div>
      </form>
    </div>
  );
};
